import * as React from 'react';
import { observer } from 'mobx-react';
import { InventoryStatus } from '../../internal/constants/InventoryStatuses';
import { FIND_IN_STORE_BUTTON } from '@estee/elc-service-view-names';
import { ServiceView, translate, addConfig, configConsumer, addContext } from '@estee/elc-service';
import { CtaTypes } from '../../internal/constants/CTATypes';
import { Sku } from '../../internal/domain/entities/Sku';
import { AddToCollectionButton } from '../../internal/views/product-cta/AddToCollectionButton';
import { LimitedRemainingMessage } from '../../internal/views/product-limited-remaining/LimitedRemainingMessage';
import { NotifyMeCTA, IProductNotifyMeCTA } from '../../internal/views/product-cta/NotifyMeCTA';
import { classes } from '../../internal/utils/Classes';
import { IProductCTA } from '../../internal/interfaces/IProductCTA';
import { ProductCTALink } from '../../internal/views/product-cta/ProductCTALink';
import { AddToBagButton } from '../../internal/views/product-cta/AddToBagButton';
import { ShopNowButton } from '../../internal/views/product-cta/ShopNowButton';
import { PreorderButton } from '../../internal/views/product-cta/PreorderButton';
import { CTAButtonWrapper } from '../../internal/views/product-cta/ProductCTAStyles';
import { mergeConfigs, mergeProductCTAConfigs } from '../../internal/utils/ConfigUtils';
import { serviceName } from '../../service-setup/packageInfo';
import { ProductCTAViewController } from '../../internal/controllers/ProductCTAViewController';
import { buttonLabels } from '../../internal/utils/ProductCTALabels';
import classnames from 'classnames';

export const enableByInventoryStatus = (enabledByStatus: string[], inventoryStatus: string) => {
    return (
        enabledByStatus.includes(inventoryStatus) ||
        (enabledByStatus.length === 0 && inventoryStatus !== InventoryStatus.SoldOut)
    );
};

type ICTAButtons = {
    [key in CtaTypes]?: JSX.Element | null;
};

@addContext()
@addConfig(['prodcatConfig', 'features'], mergeConfigs)
@configConsumer(
    ['waitlist', 'features', 'isCoreSite', 'preorderDateFormat', 'addToBag'],
    mergeProductCTAConfigs
)
@translate([
    'shopNowButtonLabel',
    'ctaLinkLabel',
    'addToBagButtonLabel',
    'addedToCartLabel',
    'availableOn',
    'preOrder',
    'outOfStockButtonLabel',
    'soldOutButtonLabel',
    'comingSoonButtonLabel',
    'taxIncluded'
])
@observer
class ProductCTA extends React.Component<IProductCTA> {
    public static displayName: string;
    private viewController: ProductCTAViewController;

    constructor(props: IProductCTA) {
        super(props);
        this.viewController = props.viewController;
        this.viewController.initialize(props.config);
    }

    public async componentDidMount() {
        await this.viewController.loaded(this.props.config.selectedSku);
    }

    componentDidUpdate(prevProps: IProductCTA) {
        const selectedSkuId = this.props.config.selectedSku?.skuId;
        if (selectedSkuId !== prevProps.config.selectedSku?.skuId) {
            this.viewController.setSelectedSkuId(selectedSkuId as string);
        }
    }

    private onAddToBagClick = async () => {
        const { addToBag, selectedSku } = this.viewController;
        const {
            skuId = selectedSku.skuId,
            quantity,
            replenishment,
            showSuccessLabel
        } = this.props.config;
        const appliedShowSuccessLabel = showSuccessLabel !== undefined ? showSuccessLabel : true;

        await addToBag(skuId, quantity, appliedShowSuccessLabel, replenishment);

        if (this.props.config.closeModalAction) {
            setTimeout(this.props.config.closeModalAction, 500);
        }
    };

    private onAddToCollectionButtonClick = async () => {
        const { selectedSku } = this.viewController;
        const { quantity, skuId = selectedSku.skuId } = this.props.config;

        await this.viewController.addSkuToCollection(skuId, quantity);
    };

    private getPreorderButton() {
        const { config: buttonConfig, translations } = this.props;
        const { getStickyButtonLabel, preorderAvailabilityLabel } = this.viewController;
        const selectedSku: Sku = buttonConfig.selectedSku || this.viewController.data.selectedSku;

        const ctaProps = {
            onPreorderButtonClick: this.onAddToBagClick,
            getStickyButtonLabel,
            preorderAvailabilityLabel,
            selectedSku: selectedSku,
            translations
        };

        return (
            <div className={this.props.config.className}>
                <PreorderButton {...ctaProps} />
            </div>
        );
    }

    private getAddToBagButton() {
        const { config, translations } = this.props;
        const selectedSku: Sku = config.selectedSku || this.viewController.data.selectedSku;
        const { getStickyButtonLabel } = this.viewController;
        const {
            isShowingSuccessLabel,
            addingToBag,
            disableAddToBag,
            butttonLabelOverride
        } = this.viewController.data;
        const butttonLabelOverridebyInvetoryStatus =
            butttonLabelOverride && translations
                ? buttonLabels(translations)[selectedSku.inventoryStatus]
                : butttonLabelOverride;

        const ctaProps = {
            onAddToBagClick: this.onAddToBagClick,
            getStickyButtonLabel,
            translations,
            selectedSku: selectedSku,
            isShowingSuccessLabel,
            addingToBag,
            disableAddToBag,
            butttonLabelOverride: butttonLabelOverridebyInvetoryStatus,
            componentType: config.componentType
        };

        return (
            <div className={this.props.config.className}>
                <AddToBagButton {...ctaProps} />
            </div>
        );
    }

    private getAddToCollectionButton() {
        const { addingToBag, selectedSku, isOnCollection } = this.viewController.data;
        const { className } = this.props.config;

        const addToCollectionProps = {
            onAddToCollectionClick: this.onAddToCollectionButtonClick,
            closeModalAction: this.props.config.closeModalAction,
            translations: this.props.translations,
            selectedSku,
            addingToBag,
            isOnCollection
        };

        return (
            <div className={className}>
                <AddToCollectionButton {...addToCollectionProps} />
            </div>
        );
    }

    private getFindInStoreButton() {
        const findInStoreClassNames = classes('find-in-store-button');
        const { skuId } = this.props.config;

        return (
            <div className={findInStoreClassNames}>
                <ServiceView
                    name={FIND_IN_STORE_BUTTON}
                    serviceName={serviceName}
                    config={{ skuId }}
                />
            </div>
        );
    }

    private getShopNowButton() {
        const {
            viewController: { navigateToProduct }
        } = this;

        return <ShopNowButton onShopNowClick={navigateToProduct} />;
    }

    private getNotifyMeCTA() {
        const selectedSku = this.props.config.selectedSku || this.viewController.data.selectedSku;

        const notifyMeCTAProps: IProductNotifyMeCTA = {
            selectedSku: selectedSku
        };

        return <NotifyMeCTA {...notifyMeCTAProps} />;
    }

    private getCTAButton() {
        const {
            viewController: {
                data: { ctaType }
            }
        } = this;

        const ctaButtons: ICTAButtons = {
            add_to_bag: this.getAddToBagButton(),
            add_to_collection: this.getAddToCollectionButton(),
            default: this.getAddToBagButton(),
            hide_cta: null,
            find_in_store: this.getFindInStoreButton(),
            notify_me: this.getNotifyMeCTA(),
            preorder: this.getPreorderButton(),
            shop_now: this.getShopNowButton()
        };

        const ctaButton = ctaButtons[ctaType as CtaTypes];

        return ctaButton;
    }

    public render() {
        if (!this.viewController.isLoaded) {
            return null;
        }

        const { enableProductDetailLink, ctaType } = this.props.config;
        const displayCTAButton = ctaType !== CtaTypes.HIDE_CTA;
        const {
            data: { selectedSku },
            navigateToProduct,
            enableLimitedRemaining
        } = this.viewController;
        const { highVelocityQuantity = 0 } = selectedSku;
        const productCTAButtonWrapperClassNames = classes('product-cta-button-wrapper');
        const ctaTypeClassName = this.props.viewController.sanitizeClassName(
            selectedSku.inventoryStatus
        );

        const productCTAWrapperClassNames = classnames(classes('product-cta-wrapper'), {
            [ctaTypeClassName]: true
        });

        return (
            <div className={productCTAWrapperClassNames}>
                {displayCTAButton && (
                    <CTAButtonWrapper className={productCTAButtonWrapperClassNames}>
                        {this.getCTAButton()}
                    </CTAButtonWrapper>
                )}
                {enableProductDetailLink && (
                    <ProductCTALink navigateToProduct={navigateToProduct} />
                )}
                {enableLimitedRemaining && (
                    <LimitedRemainingMessage skuCountRemaining={highVelocityQuantity} />
                )}
            </div>
        );
    }
}

ProductCTA.displayName = 'ProductCTA';

export default ProductCTA;
